// OPERATOR-CONSOLE: the small form kit the setting modals share — labelled number, text, money,
// select and check fields, a row editor for arrays (visible Add and Remove), a key/value table for
// showing a value, and a structural form for plain nested objects (numbers, booleans, text).
import {
  Button,
  Checkbox,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { type ReactNode, useId } from "react";
import { dollars, microsToInput, parseDollars } from "../../billing/billing-money";
import { DENSE_TABLE, RowAction } from "../../settings/row-actions";

export type Obj = Record<string, unknown>;

/** "kanban_wake.max_wakes_per_hour" → "Max wakes per hour". */
export function labelOf(path: string): string {
  const last = path.split(".").at(-1) ?? path;
  const words = last.replace(/_micros$|_minor$/, "").replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A leaf value as text in a table. */
export function leafText(path: string, v: unknown): string {
  if (v === null || v === undefined) return "None";
  if (typeof v === "boolean") return v ? "On" : "Off";
  if (typeof v === "number") {
    if (path.endsWith("_micros")) return dollars(v);
    if (path.endsWith("_minor")) return dollars(v * 10_000);
    return v.toLocaleString("en-US");
  }
  if (Array.isArray(v)) return v.length === 0 ? "None" : v.map((x) => String(x)).join(", ");
  return String(v);
}

/** Every leaf of a nested object as `a.b` → value (arrays are leaves). */
export function flatten(v: unknown, prefix = ""): [string, unknown][] {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return [[prefix, v]];
  return Object.entries(v as Obj).flatMap(([k, x]) => {
    const path = prefix ? `${prefix}.${k}` : k;
    return x !== null && typeof x === "object" && !Array.isArray(x)
      ? flatten(x, path)
      : [[path, x] as [string, unknown]];
  });
}

/** A compact two-column key/value list. */
export function KvList({
  rows,
  slot,
}: {
  rows: { label: ReactNode; value: ReactNode; key: string }[];
  slot?: string;
}) {
  return (
    <dl
      className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[minmax(10rem,auto)_1fr]"
      data-slot={slot ?? "kv"}
    >
      {rows.map((r) => (
        <div key={r.key} className="contents" data-kv={r.key}>
          <dt className="text-muted-foreground">{r.label}</dt>
          <dd className="min-w-0 break-words">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A nested object shown as a key/value list (`a.b` labelled by its last part, grouped by `a`). */
export function ObjectView({ value, skip = [] }: { value: unknown; skip?: readonly string[] }) {
  const rows = flatten(value)
    .filter(([p]) => !skip.some((s) => p === s || p.startsWith(`${s}.`)))
    .map(([p, v]) => {
      const group = p.includes(".") ? `${labelOf(p.split(".")[0] ?? "")} · ` : "";
      return { key: p, label: `${group}${labelOf(p)}`, value: leafText(p, v) };
    });
  return <KvList rows={rows} />;
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        value={Number.isFinite(value) ? String(value) : ""}
        min={min}
        max={max}
        step={step ?? 1}
        onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
      />
    </Field>
  );
}

/** A text input; with `nullable`, empty means null. */
export function TextField({
  label,
  value,
  onChange,
  nullable,
  placeholder,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
  nullable?: boolean;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        value={value ?? ""}
        {...(placeholder ? { placeholder } : {})}
        onChange={(e) => {
          const v = e.target.value;
          onChange(nullable && v.trim() === "" ? null : v);
        }}
      />
    </Field>
  );
}

/** Dollars in an input; the value is micros (or minor units with `minor`). Null when empty. */
export function MoneyField({
  label,
  value,
  onChange,
  minor,
  nullable,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  minor?: boolean;
  nullable?: boolean;
}) {
  const id = useId();
  const micros = value === null ? null : minor ? value * 10_000 : value;
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        inputMode="decimal"
        defaultValue={micros === null ? "" : microsToInput(micros)}
        {...(nullable ? { placeholder: "None" } : {})}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === "" && nullable) return onChange(null);
          const m = parseDollars(raw);
          onChange(m === null ? Number.NaN : minor ? Math.round(m / 10_000) : m);
        }}
      />
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <NativeSelect
        id={id}
        value={value}
        className="w-full"
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map((o) => (
          <NativeSelectOption key={o.value} value={o.value}>
            {o.label}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    </Field>
  );
}

export function CheckField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      {label}
    </label>
  );
}

export type Column<R> = {
  key: keyof R & string;
  label: string;
  kind: "text" | "nullable" | "number" | "check" | "select";
  options?: readonly { value: string; label: string }[];
  placeholder?: string;
};

/** An array of flat rows: a table of inputs, a visible Remove per row and an Add button. */
export function RowsEditor<R extends Obj>({
  label,
  rows,
  columns,
  blank,
  onChange,
  addLabel,
  slot,
}: {
  label: string;
  rows: readonly R[];
  columns: readonly Column<R>[];
  blank: () => R;
  onChange: (next: R[]) => void;
  addLabel: string;
  slot?: string;
}) {
  const set = (i: number, k: string, v: unknown) =>
    onChange(rows.map((r, j) => (j === i ? ({ ...r, [k]: v } as R) : r)));
  return (
    <div className="flex min-w-0 flex-col gap-2" data-slot={slot ?? "rows-editor"}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <Button
          type="button"
          size="xs"
          variant="secondary"
          onClick={() => onChange([...rows, blank()])}
        >
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          {addLabel}
        </Button>
      </div>
      <Table className={DENSE_TABLE}>
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead key={c.key} scope="col">
                {c.label}
              </TableHead>
            ))}
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional drafts
            <TableRow key={i} data-row={i}>
              {columns.map((c) => {
                const v = r[c.key];
                const name = `${c.label} ${i + 1}`;
                return (
                  <TableCell key={c.key} className="align-middle">
                    {c.kind === "check" ? (
                      <Checkbox
                        aria-label={name}
                        checked={v === true}
                        onCheckedChange={(x) => set(i, c.key, x === true)}
                      />
                    ) : c.kind === "select" ? (
                      <NativeSelect
                        aria-label={name}
                        size="sm"
                        value={v === null ? "" : String(v)}
                        onChange={(e) =>
                          set(i, c.key, e.target.value === "" ? null : e.target.value)
                        }
                      >
                        {(c.options ?? []).map((o) => (
                          <NativeSelectOption key={o.value} value={o.value}>
                            {o.label}
                          </NativeSelectOption>
                        ))}
                      </NativeSelect>
                    ) : (
                      <Input
                        aria-label={name}
                        {...(c.kind === "number" ? { type: "number" } : {})}
                        value={v === null || v === undefined ? "" : String(v)}
                        placeholder={c.placeholder ?? (c.kind === "nullable" ? "Default" : "")}
                        onChange={(e) => {
                          const x = e.target.value;
                          set(
                            i,
                            c.key,
                            c.kind === "number"
                              ? Number(x)
                              : c.kind === "nullable" && x.trim() === ""
                                ? null
                                : x,
                          );
                        }}
                      />
                    )}
                  </TableCell>
                );
              })}
              <TableCell className="w-px">
                <RowAction
                  icon={Trash2Icon}
                  label={`Remove row ${i + 1}`}
                  tip="Remove"
                  onClick={() => onChange(rows.filter((_, j) => j !== i))}
                />
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} className="text-muted-foreground">
                None.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * A plain nested object as fields: numbers, booleans, text (null text: empty), enums as selects.
 * Paths in `skip` are left as they are.
 */
export function ObjectForm({
  value,
  onChange,
  enums = {},
  skip = [],
  money = [],
}: {
  value: Obj;
  onChange: (next: Obj) => void;
  enums?: Record<string, readonly string[]>;
  skip?: readonly string[];
  /** Paths edited as dollars (micros, or minor units for `_minor`). */
  money?: readonly string[];
}) {
  const setAt = (path: string, v: unknown) => {
    const parts = path.split(".");
    const write = (o: Obj, i: number): Obj => {
      const k = parts[i] as string;
      if (i === parts.length - 1) return { ...o, [k]: v };
      return { ...o, [k]: write((o[k] ?? {}) as Obj, i + 1) };
    };
    onChange(write(value, 0));
  };
  const leaves = flatten(value).filter(
    ([p]) => !skip.some((s) => p === s || p.startsWith(`${s}.`)),
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {leaves.map(([p, v]) => {
        const group = p.includes(".") ? `${labelOf(p.split(".")[0] ?? "")} · ` : "";
        const label = `${group}${labelOf(p)}`;
        if (enums[p])
          return (
            <SelectField
              key={p}
              label={label}
              value={String(v)}
              options={(enums[p] ?? []).map((o) => ({ value: o, label: labelOf(o) }))}
              onChange={(x) => setAt(p, x)}
            />
          );
        if (money.includes(p))
          return (
            <MoneyField
              key={p}
              label={label}
              value={typeof v === "number" ? v : null}
              minor={p.endsWith("_minor")}
              nullable={v === null}
              onChange={(x) => setAt(p, x)}
            />
          );
        if (typeof v === "boolean")
          return (
            <div key={p} className="flex items-end pb-2">
              <CheckField label={label} checked={v} onChange={(x) => setAt(p, x)} />
            </div>
          );
        if (typeof v === "number")
          return <NumberField key={p} label={label} value={v} onChange={(x) => setAt(p, x)} />;
        return (
          <TextField
            key={p}
            label={label}
            value={typeof v === "string" ? v : null}
            nullable
            {...(v === null ? { placeholder: "None" } : {})}
            onChange={(x) => setAt(p, x)}
          />
        );
      })}
    </div>
  );
}
